import React, { useState } from 'react';
import { X, Play, Mail, User } from 'lucide-react';

const WATCH_URL = "https://cb559ingmmfcbb27sipnaq4u5q.hop.clickbank.net";
const GOOGLE_SHEET_URL = "https://script.google.com/macros/s/AKfycbwsC20p-VB3VRGLXbDABn0JOOaFPbd2zJR1PH_AOxGwWzFkFgwGZArjfLN5X1IpgT-fYw/exec";

const OptInModal = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const saveToGoogleSheet = async (name, email) => {
    try {
      await fetch(GOOGLE_SHEET_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email }),
      });
      return true;
    } catch (error) {
      console.error('Error saving to Google Sheet:', error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Please enter your name';
    }

    if (!email.trim()) {
      newErrors.email = 'Please enter your email';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      
      // Save to Google Sheet
      await saveToGoogleSheet(name, email);
      
      // Store in localStorage as backup
      localStorage.setItem('userOptIn', JSON.stringify({ name, email, timestamp: new Date().toISOString() }));
      
      // Open the video URL
      setTimeout(() => {
        window.open(WATCH_URL, '_blank', 'noopener,noreferrer');
        setIsSubmitting(false);
        onClose();
        setName('');
        setEmail('');
      }, 500);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-md w-full p-8 relative shadow-2xl animate-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-[#cc0000] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Play className="w-10 h-10 text-white ml-1" fill="white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Watch The Presentation Now!
          </h2>
          <p className="text-gray-600">
            Enter your details below to access the exclusive video presentation about the Energy Revolution System
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Your Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#cc0000] focus:border-transparent transition-all ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Your Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#cc0000] focus:border-transparent transition-all ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-3 bg-[#cc0000] hover:bg-[#aa0000] disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg shadow-lg transform hover:scale-105 disabled:hover:scale-100 transition-all duration-300 text-xl"
          >
            <Play className="w-6 h-6" fill="white" />
            {isSubmitting ? 'Loading...' : 'Watch Video Now'}
          </button>
        </form>

        {/* Privacy Note */}
        <p className="text-center text-gray-500 text-xs mt-4">
          Your information is 100% secure and will never be shared with anyone.
        </p>
      </div>

      <style>{`
        .animate-in {
          animation: modalIn 0.3s ease-out;
        }
        @keyframes modalIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default OptInModal;
